import { fa, type MessageKey } from './fa';

export type { MessageKey };

export const locale = 'fa-IR';
export const dir = 'rtl' as const;
export const brandFa = fa.brand;

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

type Params = Record<string, string | number>;

/** Map Latin digits to Persian digits. */
export function toFaDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]);
}

/** Map Persian/Arabic-Indic digits to Latin digits. */
export function fromFaDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632));
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return toFaDigits(new Intl.NumberFormat(locale, options).format(value));
}

export function t(key: MessageKey, params?: Params): string {
  const template: string = fa[key];
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const raw = params[name];
    if (raw === undefined || raw === null) return '';
    if (typeof raw === 'number') return formatNumber(raw);
    return toFaDigits(String(raw));
  });
}

export function labelOf(
  prefix: 'role' | 'status' | 'dance' | 'eventType' | 'level' | 'kind',
  value: string,
): string {
  const key = `${prefix}.${value}` as MessageKey;
  return key in fa ? t(key) : value.replace(/_/g, ' ');
}

export function formatDate(unixSeconds: number, options?: Intl.DateTimeFormatOptions): string {
  return toFaDigits(
    new Date(unixSeconds * 1000).toLocaleDateString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...options,
    }),
  );
}

export function formatTime(unixSeconds: number): string {
  return toFaDigits(
    new Date(unixSeconds * 1000).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }),
  );
}

export function formatWhen(unixSeconds: number): string {
  return toFaDigits(
    new Date(unixSeconds * 1000).toLocaleString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  );
}

export function applyDocumentLocale() {
  document.documentElement.lang = 'fa';
  document.documentElement.dir = dir;
}
